import { Animated, StyleSheet, View } from 'react-native';

import { IconButton } from '../primitives/IconButton';
import type { PressGesture } from '../primitives/Chip';
import { useAttentionPulse } from '../attentionCue';
import { formatRelativeAge } from '../formatRelativeAge';
import { useReducedMotion } from '../useReducedMotion';
import type { TFn } from '../i18nTypes';

import type { useTheme } from './theme';
import { useExerciseAgePulseEnabled } from './state/exerciseAgePulsePreference';
import { useExerciseHistoryView } from './useExerciseHistoryView';

// LG-49: the history icon, which BLINKS when this exercise has been left
// alone for a while.
//
// ⛔ The blink is on the icon ITSELF. Antenore, 2026-09-01, after the first
// build put a separate badge next to it: *«hai aggiunto un altro chip con
// scritto dentro quanto è vecchia invece di far blinkare l'icona della
// cronologia come ti avevo detto»*. The card already carries a superset tab
// row, a title, six qualifier chips and a meter; a new standing element is
// one more thing to look at, and the control that opens the history is
// exactly the thing the signal is about. So the control is the signal.
//
// Two levels, his thresholds, measured on 22 330 sets:
//   warn   — you are training, just not this (28 days off the exercise)
//   danger — you were away from the gym too (a layoff over 14 days on top)
//
// The AGE IN WORDS did not disappear, it moved into the accessible name
// ("Storico, ultima volta 6 sett. fa"), so a screen reader still gets the
// fact the tint carries. Nothing new is drawn.
//
// ⛔ Renders the plain icon when the exercise is fresh or never done:
// "never" is not an age, and the panel's estimate is the sentence for it.

/** Gap between blinks. Rare on purpose: he accepted the intermittent form
 *  BECAUSE it fires seldom, and there is a switch for the rest. */
const PULSE_EVERY_MS = 15_000;

export function HistoryIconButton({
  exerciseId,
  active,
  onPress,
  gesture,
  accessibilityHint,
  theme,
  t,
}: {
  exerciseId: string;
  /** History panel open — also stops the cue: it has been read. */
  active: boolean;
  onPress: () => void;
  /** #308: the card's hold-for-hint bundle (useTransientTip.holdProps). */
  gesture?: PressGesture;
  accessibilityHint?: string;
  theme: ReturnType<typeof useTheme>;
  t: TFn;
}) {
  const { staleness } = useExerciseHistoryView(exerciseId);
  const { enabled } = useExerciseAgePulseEnabled();
  const reducedMotion = useReducedMotion();

  const level = staleness.level;
  const stale = level === 'warn' || level === 'danger';

  const pulse = useAttentionPulse({
    active: stale && !active,
    enabled,
    reducedMotion,
    cueKey: exerciseId,
    everyMs: PULSE_EVERY_MS,
  });

  const styles = makeStyles(theme);
  const age = stale ? formatRelativeAge(t, staleness.daysSinceExercise) : null;

  const label = t('mobile.logger.historyToggle', { defaultValue: 'History' });
  const button = (
    <IconButton
      name="file-clock"
      active={active}
      onPress={onPress}
      accessibilityLabel={
        age === null
          ? label
          : t('mobile.logger.historyToggleAged', {
              defaultValue: 'History, last done {{age}}',
              age,
            })
      }
      gesture={gesture}
      accessibilityHint={accessibilityHint}
      hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
      theme={theme}
    />
  );

  if (!stale) return button;

  return (
    <View style={styles.wrap}>
      {/* The cue is a tinted ring around the glyph, pulsing. ⛔ NOT the
          icon's own opacity: fading the control out would take a tap target
          off screen between beats. Opacity-only → native driver. */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.cue,
          {
            opacity: pulse,
            borderColor:
              level === 'danger' ? theme.palette.danger : theme.palette.warn,
          },
        ]}
      />
      {button}
    </View>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    wrap: { alignSelf: 'center' },
    cue: {
      position: 'absolute',
      top: -2,
      bottom: -2,
      left: -2,
      right: -2,
      borderWidth: 2,
      borderRadius: theme.radius.r2,
    },
  });
